// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import type { TyLangValue, TyLatLng, TyProject, TyProjectStage, TyUnitType } from "@/types";
import Icon from "@/UI/atoms/Icon";
import MultiLangInput from "@/UI/inputs/MultiLangInput";
import FileInput from "@/UI/inputs/FileInput";
import ImagePickerInput from "@/UI/inputs/ImagePickerInput";
import ChoiceInput from "@/UI/inputs/ChoiceInput";
import MapInput from "@/UI/inputs/MapInput";
import MultiSelectInput from "@/UI/inputs/MultiSelectInput";
import SwitchInput from "@/UI/inputs/SwitchInput";
import TextInput from "@/UI/inputs/TextInput";
// --- Logic --------------------------------------------------------------------------------------
export type TyProjectFormDetails = { project?: TyProject | null; onDone: () => void };

type Props = { onClose: () => void; details?: unknown };

/**
 * Create or edit a project. One form for both, because the difference is a method and an id.
 *
 * Almost nothing is required — only the name. A company builds its data over weeks, and a form that
 * demands a plan number up front is a form people work around.
 *
 * Photos are handed to `useUploads` **after** the record is saved and the form is closed. A project
 * exists the moment it is saved; waiting on a 40 MB render before it does is the UX we removed.
 */
export default function ProjectFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { project, onDone } = details as TyProjectFormDetails;
    const enqueue = useUploads((state) => state.enqueue);
    const isEdit = !!project;

    const [name, setName] = useState<TyLangValue[]>(project?.name ?? []);
    const [description, setDescription] = useState<TyLangValue[]>(project?.description ?? []);
    const [slug, setSlug] = useState(project?.slug ?? "");
    const [types, setTypes] = useState<string[]>(project?.types ?? []);
    const [stage, setStage] = useState<string>(project?.stage ?? "");
    const [city, setCity] = useState(project?.city ?? "");
    const [address, setAddress] = useState(project?.address ?? "");
    const [location, setLocation] = useState<TyLatLng | null>(project?.location ?? null);
    const [planNumber, setPlanNumber] = useState(project?.planNumber ?? "");
    const [plotNumber, setPlotNumber] = useState(project?.plotNumber ?? "");
    const [wafiNumber, setWafiNumber] = useState(project?.wafiNumber ?? "");
    const [notes, setNotes] = useState(project?.notes ?? "");
    const [isPublic, setIsPublic] = useState(!!project?.isPublic);
    const [photos, setPhotos] = useState<File[]>([]);
    /** The chosen pictures, in order. A gallery has a first one; the slot alone could not say which. */
    const [gallery, setGallery] = useState<string[]>(project?.gallery ?? []);
    const [formError, setFormError] = useState<string | null>(null);

    const path = isEdit ? `/properties/projects/${project!._id}` : "/properties/projects";
    const { data, loading, error, succeededAt, invoke } = useFetch<{ project: TyProject }>(path, {}, false);

    useEffect(() => {
        if (!succeededAt || !data) return;
        // The record exists now, so its files can be attached to it and left to the tray.
        if (photos.length) enqueue(photos, { model: "Project", id: data.project._id, slot: "gallery" });
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = () => {
        setFormError(null);
        if (!name.some((entry) => entry.value.trim())) return setFormError(t.properties.nameRequired);

        invoke({
            method: isEdit ? "PATCH" : "POST",
            data: {
                name,
                description,
                // Empty means "generate one from the id" — only on create; an edit keeps what it has.
                ...(slug.trim() || !isEdit ? { slug: slug.trim() || undefined } : {}),
                types,
                stage: stage || null,
                city: city.trim() || null,
                address: address.trim() || null,
                location,
                planNumber: planNumber.trim() || null,
                plotNumber: plotNumber.trim() || null,
                wafiNumber: wafiNumber.trim() || null,
                notes: notes.trim() || null,
                // Only when editing: on create the project has no files yet to choose between.
                ...(isEdit ? { gallery } : {}),
                isPublic,
            },
        });
    };

    return (
        <section className="side-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{isEdit ? t.properties.editProject : t.properties.addProject}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <MultiLangInput value={name} onChange={setName} label={t.properties.fields.name} disabled={loading} />

                {/* The catalog link and the switch that publishes it are one decision. The switch
                    is only as wide as its own words, so the link keeps the rest of the row. */}
                <div className="side-form__row side-form__row--tail">
                    <TextInput
                        value={slug}
                        onChange={setSlug}
                        label={t.properties.fields.slug}
                        placeholder={t.properties.fields.slugHint}
                        dir="ltr"
                        disabled={loading}
                    />
                    <SwitchInput value={isPublic} onChange={setIsPublic} label={t.properties.fields.showInCatalog} disabled={loading} />
                </div>

                <MultiSelectInput
                    value={types}
                    onChange={setTypes}
                    options={constants.unitTypes.map((type) => ({ value: type, label: t.properties.unitTypes[type as TyUnitType] }))}
                    label={t.properties.fields.types}
                    disabled={loading}
                />

                <div className="side-form__row side-form__row--lead">
                    <ChoiceInput
                        value={stage}
                        onChange={setStage}
                        options={constants.projectStages.map((item) => ({ value: item, label: t.properties.stages[item as TyProjectStage] }))}
                        label={t.properties.fields.stage}
                        disabled={loading}
                    />
                    <TextInput value={city} onChange={setCity} label={t.properties.fields.city} disabled={loading} />
                </div>
                <TextInput value={address} onChange={setAddress} label={t.properties.fields.address} disabled={loading} />

                <MapInput value={location} onChange={setLocation} label={t.properties.fields.location} disabled={loading} />

                <div className="side-form__row">
                    <TextInput value={planNumber} onChange={setPlanNumber} label={t.properties.fields.planNumber} disabled={loading} />
                    <TextInput value={plotNumber} onChange={setPlotNumber} label={t.properties.fields.plotNumber} disabled={loading} />
                    <TextInput value={wafiNumber} onChange={setWafiNumber} label={t.properties.fields.wafiNumber} disabled={loading} />
                </div>

                <MultiLangInput value={description} onChange={setDescription} label={t.properties.fields.description} multiline disabled={loading} />

                {/*
                    Creating takes photographs off the disk — a file needs an owner, and there is no
                    project yet; they show as soon as they land, because the API answers the gallery
                    from the files. Editing re-picks them in order, which is how the cover is chosen:
                    that order is the one thing the record itself stores.
                */}
                {isEdit ? (
                    <ImagePickerInput
                        owner={{ model: "Project", id: project!._id }}
                        slot="gallery"
                        value={gallery}
                        onChange={setGallery}
                        label={t.drive.slots.gallery}
                        disabled={loading}
                    />
                ) : (
                    <FileInput files={photos} onChange={setPhotos} accept="image/*" label={t.drive.slots.gallery} />
                )}

                <TextInput value={notes} onChange={setNotes} label={t.properties.fields.notes} multiline disabled={loading} />

                {(formError || error) && <p className="side-form__error">{formError ?? error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={loading}>
                    {loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
